// NEEDS YOU, the rule (docs/design/inbox.md §8): which messages stand in
// the group, in which order, and with which number.
//
// The time rail of `needsyou.ts` and the inbox pins of
// `fast/chart/pieces/inbox-pins.ts` both read this ONE answer, so the
// number of an item on the rail is always the number of its pin on the map.
// The module is pure: it reads the list and the clock, and it holds the
// fold of the group for the life of the page.

import type { InboxMsg } from '../../gen/proto/aquaphobia';

/** One item of NEEDS YOU. */
export interface NeedsItem {
  msg: InboxMsg;
  /** The number on the rail and on the pin, 1 for the newest item with a
   *  cell; 0 for an item with no cell, which draws a mark with no number
   *  and no pin. */
  num: number;
}

/** One answer of the rule: the items, and the moment it changes alone. */
export interface NeedsList {
  items: readonly NeedsItem[];
  /** How many items carry a number, and so a pin. */
  pins: number;
  /** The first wall second at which a siege window of the list ends, or
   *  Infinity: the list changes then with no new Inbox frame. */
  nextEnd: number;
}

/**
 * Why a message needs its reader, as the server's `game.Player.Needs`
 * reads it (inbox.md §4): an open REPLACE on an unread message, a captain
 * of the empire in an enemy Brig, or a siege window still to come. The
 * client cannot see whether the captain is still held, so a captured fate
 * names the kind, and the server's `needs` flag says it still holds.
 * `other` is a message the client cannot name.
 */
export type NeedsKind = 'replace' | 'captive' | 'siege' | 'other';

/** The NEEDS kind of a message, in the order the server tests them. */
export function needsKind(m: InboxMsg): NeedsKind {
  const r = m.replace;
  if (r && !r.queued && !m.read) return 'replace';
  if (m.params.fate === 'captured' && m.params.captain_id) return 'captive';
  if (windowEnd(m) > 0) return 'siege';
  return 'other';
}

/** Does the item draw in the bad ink, on the rail and on its pin? Each of
 *  the three NEEDS kinds is a loss or a threat; a message the client
 *  cannot name draws in the accent. No item takes gold: gold names a
 *  prize only (R1798). */
export function needsBad(m: InboxMsg): boolean {
  return needsKind(m) !== 'other';
}

/** The end of a siege window a message names, in wall seconds, or 0. */
function windowEnd(m: InboxMsg): number {
  const end = Number(m.params.window_end ?? 0);
  return Number.isFinite(end) ? end : 0;
}

/**
 * The items of NEEDS YOU, newest first.
 *
 * The server sets `needs` on each send of the list (inbox.md §4). A siege
 * window can end between two sends, and a window that has ended needs
 * nobody, so the rule drops it at its end on the wall clock. The stamp
 * orders the list and the id breaks a tie, so two equal stamps keep one
 * order. Only an item with a cell takes a number.
 */
export function needsItems(inbox: readonly InboxMsg[], nowSec = Date.now() / 1000): NeedsList {
  const kept: InboxMsg[] = [];
  let nextEnd = Infinity;
  for (const m of inbox) {
    if (!m.needs) continue;
    const end = windowEnd(m);
    if (end > 0 && end <= nowSec) continue;
    if (end > 0) nextEnd = Math.min(nextEnd, end);
    kept.push(m);
  }
  kept.sort((a, b) => b.unix - a.unix || b.id - a.id);
  let pins = 0;
  const items = kept.map((msg) => ({ msg, num: msg.cell !== 0 ? ++pins : 0 }));
  return { items, pins, nextEnd };
}

/** The last answer, kept while the list and the clock leave it standing. */
let memo: { inbox: readonly InboxMsg[]; at: number; list: NeedsList } | null = null;

/**
 * The items of NEEDS YOU, kept per list (§10.3).
 *
 * The chart gate asks on every frame and the panel at 4 Hz, so the answer
 * is built again only for a new list, at the end of a siege window, or for
 * a clock that went back. The caller must not change the answer.
 */
export function needsOf(inbox: readonly InboxMsg[], nowSec = Date.now() / 1000): NeedsList {
  if (memo && memo.inbox === inbox && nowSec >= memo.at && nowSec < memo.list.nextEnd) {
    return memo.list;
  }
  const list = needsItems(inbox, nowSec);
  memo = { inbox, at: nowSec, list };
  return list;
}

/** Is the group folded into its head line? It starts open, and the fold
 *  holds for the life of the page. */
let folded = false;

export function needsFolded(): boolean { return folded; }

export function setNeedsFolded(on: boolean): void { folded = on; }

/**
 * The lit item (comp C's focal point): the item the mouse points at, the
 * item that holds the keyboard focus, and the item the reader pressed
 * last, each a message id or 0. Its pin on the map draws a second ring.
 */
const lit = { hover: 0, focus: 0, pressed: 0 };

export function setNeedsHover(id: number): void { lit.hover = id; }

export function setNeedsFocus(id: number): void { lit.focus = id; }

/** The item the reader pressed last keeps its pin lit while the INBOX
 *  stands, so a telephone, which has no hover, lights the opened item. */
export function setNeedsPressed(id: number): void { lit.pressed = id; }

/** The pressed item, which the rail marks as lit. */
export function needsPressed(): number { return lit.pressed; }

/**
 * The message whose pin is lit, or 0: the pointed item, else the focused
 * one, else the last pressed one. A shut INBOX lights nothing and forgets
 * the pressed item, so the next open starts unlit.
 */
export function needsLit(inboxOpen: boolean): number {
  if (!inboxOpen) { lit.pressed = 0; return 0; }
  return lit.hover || lit.focus || lit.pressed;
}
